import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { PendingTransactionsIcon } from '@util/svgicon';
import { samRentingSessions } from '@services/api';
import { toast } from 'react-toastify';
import { differenceInSeconds } from 'date-fns';

const RentalHistory = () => {
    const [rentalHistoryData, setRentalHistoryData] = useState([]);
    const [tooltip, setTooltip] = useState({ id: null, index: null, message: '' });

    useEffect(() => {
        fetchRentalHistory();
    }, []);

    const fetchRentalHistory = async () => {
        const resp = await samRentingSessions();
        if (resp?.sam_renting_sessions?.length > 0) {
            setRentalHistoryData(resp.sam_renting_sessions);
        }
    };

    const calculateDuration = (start, end) => {
        const startTime = new Date(start);
        const endTime = new Date(end);
        const totalSeconds = differenceInSeconds(endTime, startTime);

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        return `${hours}h ${minutes}m ${seconds}s`;
    };

    const copyToClipboard = (text, index, type) => {
        navigator.clipboard.writeText(text).then(() => {
            setTooltip({ id: text, index, message: 'Copied!' });
            setTimeout(() => setTooltip({ id: null, index: null, message: '' }), 2000); // Reset tooltip after 2 seconds
            const label = type === 'wallet' ? 'Base wallet address' : 'Transaction reference';
            toast.success(`${label} copied to clipboard`);
        });
    };

    return (
        <div className="w-full h-full flex flex-col gap-[18px]">
            <div className="w-full flex pt-0 pr-[8px] pb-[8px] pl-0 justify-between items-center">
                <div className="flex items-center gap-[16px]">
                    <div className="flex items-center gap-[8px]">
                        <PendingTransactionsIcon />
                        <p className="text-[#282828] text-[18px] not-italic font-medium leading-[28px] tracking-[-0.72px]">Rent to Sam History</p>
                    </div>
                </div>
            </div>
            <table className="w-full flex flex-col py-[8px] justify-between items-center gap-[6px]">
                <thead className="w-full flex px-[8px] py-[8px] justify-between items-center rounded-[8px] bg-[#FAFAFA]">
                    <tr className="w-full flex py-[8px] justify-between items-center rounded-[8px]">
                        <th className="w-[23%] text-[#727272] text-center text-[10px] sm:text-[14px] not-italic font-medium leading-[12px] tracking-[-0.52px]">Start Time</th>
                        <th className="w-[23%] text-[#727272] text-center text-[10px] sm:text-[14px] not-italic font-medium leading-[12px] tracking-[-0.52px]">End Time</th>
                        <th className="w-[15%] text-[#727272] text-center text-[10px] sm:text-[14px] not-italic font-medium leading-[12px] tracking-[-0.52px]">Session Duration</th>
                        <th className="w-[17%] text-[#727272] text-center text-[10px] sm:text-[14px] not-italic font-medium leading-[12px] tracking-[-0.52px]">Wallet Address</th>
                        <th className="w-[22%] text-[#727272] text-center text-[10px] sm:text-[14px] not-italic font-medium leading-[12px] tracking-[-0.52px]">Payment Transaction</th>
                    </tr>
                </thead>
                <tbody className="w-full flex flex-col px-[8px] py-[8px] items-center gap-[24px] rounded-[8px] bg-[#FAFAFA]">
                    {rentalHistoryData.length > 0 ? (
                        rentalHistoryData.map((rentalHistory, index) => {
                            const rentalBaseWalletAddress = `${rentalHistory.base_wallet_address.slice(0, 4)}...${rentalHistory.base_wallet_address.slice(-4)}`;
                            const paymentTransactionRef = rentalHistory.transaction_reference
                                ? `${rentalHistory.transaction_reference.slice(0, 6)}...${rentalHistory.transaction_reference.slice(-6)}`
                                : '-';

                            return (
                                <tr key={rentalHistory.id} className="w-full flex items-center justify-center">
                                    <td className="w-[23%] text-[#727272] text-center text-[9px] sm:text-[16px] not-italic font-medium leading-[14px] tracking-[-0.32px]">
                                        {DateTime.fromISO(rentalHistory.started_at).toFormat('MMMM dd, yyyy, hh:mm a')}
                                    </td>
                                    <td className="w-[23%] text-[#727272] text-center text-[9px] sm:text-[16px] not-italic font-medium leading-[14px] tracking-[-0.32px]">
                                        {rentalHistory.ended_at ? DateTime.fromISO(rentalHistory.ended_at).toFormat('MMMM dd, yyyy, hh:mm a') : 'Ongoing'}
                                    </td>
                                    <td className="w-[15%] text-[#727272] text-center text-[9px] sm:text-[16px] not-italic font-medium leading-[14px] tracking-[-0.32px]">
                                        {rentalHistory.ended_at ? calculateDuration(rentalHistory.started_at, rentalHistory.ended_at) : 'Ongoing'}
                                    </td>
                                    <td
                                        className="w-[17%] text-[#727272] text-center text-[9px] sm:text-[16px] not-italic font-medium leading-[14px] tracking-[-0.32px] cursor-pointer relative group"
                                        onMouseEnter={() => setTooltip({ id: rentalHistory.base_wallet_address, index, message: 'Copy' })}
                                        onMouseLeave={() => setTooltip({ id: null, index: null, message: '' })}
                                        onClick={() => copyToClipboard(rentalHistory.base_wallet_address, index, 'wallet')}
                                    >
                                        {rentalBaseWalletAddress || '-'}
                                        {tooltip.id === rentalHistory.base_wallet_address && tooltip.index === index && (
                                            <div className="absolute top-[-28px] left-1/2 transform -translate-x-1/2 px-4 py-2 text-white text-xs bg-black rounded-md shadow-md">
                                                {tooltip.message}
                                                <div className="absolute bottom-[-5px] left-1/2 transform -translate-x-1/2 w-3 h-3 bg-black rotate-45"></div>
                                            </div>
                                        )}
                                    </td>
                                    <td
                                        className="w-[22%] text-[#727272] text-center text-[9px] sm:text-[16px] not-italic font-medium leading-[14px] tracking-[-0.32px] cursor-pointer relative group break-words whitespace-normal"
                                        onMouseEnter={() => setTooltip({ id: rentalHistory.transaction_reference, index, message: 'Copy' })}
                                        onMouseLeave={() => setTooltip({ id: null, index: null, message: '' })}
                                        onClick={() => copyToClipboard(rentalHistory.transaction_reference, index, 'transaction')}
                                    >
                                        {paymentTransactionRef}
                                        {tooltip.id === rentalHistory.transaction_reference && tooltip.index === index && (
                                            <div className="absolute top-[-28px] left-1/2 transform -translate-x-1/2 px-4 py-2 text-white text-xs bg-black rounded-md shadow-md">
                                                {tooltip.message}
                                                <div className="absolute bottom-[-5px] left-1/2 transform -translate-x-1/2 w-3 h-3 bg-black rotate-45"></div>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })
                    ) : (
                        <tr className="w-full flex justify-center items-center">
                            <td colSpan="5" className="text-[#727272] text-center text-[13px] sm:text-[16px] not-italic font-medium leading-[22px] tracking-[-0.32px]">No rental history found</td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default RentalHistory;